import { FC } from 'react';

import { Typography } from '@mui/material';

import { Flex } from 'src/components/Flex';

import { RateInputField, SectionCard } from '../../components';
import { ThreeToggleRates } from '../../components/ThreeToggleRates';

interface TransactionPricing {
    name: string;
    notAdminUser?: boolean;
}

export type CARDS = {
    heading: string;
    name: string;
    isConstant?: boolean;
    note?: string;
    tooltip?: string;
    hardCodedToggle?: boolean;
    isPartnerOfPartner?: boolean;
};

export const PERSONAL_CARDS: CARDS[] = [
    {
        heading: 'Maestro Int./Debit Personal International',
        name: 'maestroIntDebtPersonalInformation',
    },
    {
        heading: 'Visa Debit (Inc Electron)',
        name: 'visaDebt',
    },
    {
        heading: 'Debit Mastercard',
        name: 'debitMaster',
    },
];

export const COMMERCIAL_CARDS: CARDS[] = [
    {
        heading: 'Master Credit (Inc Maestro Int.Debit Personal Int.)',
        name: 'masterCredit',
    },
    {
        heading: 'Visa Credit (Inc Electron Personal Int./Debit Personal Int.)',
        name: 'visaCredit',
    },
    {
        heading: 'Visa (excluding Business Debit)',
        name: 'visaCorpDebit',
    },
    {
        heading: 'Visa Business Debit',
        name: 'visaBusinessDebit',
    },
    {
        heading: 'Mastercard Signia/Mastercard World',
        name: 'masterCardSigniaOrMastercardWorld',
    },
    {
        heading: 'Mastercard (All Corporate and Commercial cards)',
        name: 'masterCard',
    },
    { heading: 'VISA DR COM INTL', name: 'visaDrComInt', isConstant: true },
];

export const OTHER_CARDS: CARDS[] = [
    { heading: 'JCB', name: 'jcb' },
    { heading: 'Diners Club/Discover', name: 'dinersClubOrDiscover' },
    { heading: 'UnionPay International', name: 'unionPayInternational' },
    { heading: 'American Express (AMEX)', name: 'americanExpress' },
];

export const TransactionPricing: FC<TransactionPricing> = ({ name, notAdminUser }) => {
    return (
        <Flex flexDirection="column" gap="24px">
            <SectionCard heading="Personal Cards">
                {PERSONAL_CARDS.map((card, index) => {
                    return (
                        <ThreeToggleRates
                            key={card.name}
                            namePrefix={`${name}.personalCards.${card.name}`}
                            heading={card.heading}
                            isOdd={index % 2 === 0}
                            isConstant={card.isConstant || notAdminUser}
                        />
                    );
                })}
            </SectionCard>
            <SectionCard heading="Commercial Cards">
                {COMMERCIAL_CARDS.map((card, index) => {
                    return (
                        <ThreeToggleRates
                            key={card.name}
                            namePrefix={`${name}.commercialCards.${card.name}`}
                            heading={card.heading}
                            isOdd={index % 2 === 0}
                            isConstant={card.isConstant || notAdminUser}
                        />
                    );
                })}
            </SectionCard>
            <SectionCard heading="Other">
                {OTHER_CARDS.map((card, index) => {
                    return (
                        <ThreeToggleRates
                            key={card.name}
                            namePrefix={`${name}.otherCards.${card.name}`}
                            heading={card.heading}
                            isOdd={index % 2 === 0}
                            isConstant={card.isConstant || notAdminUser}
                        />
                    );
                })}
            </SectionCard>
            <SectionCard heading="Locality">
                <Flex flex={1} flexDirection="column">
                    <Flex justifyContent="space-between" flex={1} marginBottom="24px">
                        <Flex flex={1 / 3} justifyContent="center">
                            <Typography color="transparent" fontWeight="600">
                                placeholder
                            </Typography>
                        </Flex>
                        <Flex flex={1 / 3} justifyContent="center">
                            <Typography
                                color="#575757"
                                fontWeight="600"
                                fontSize="16px"
                                lineHeight="24px"
                                textAlign="center"
                            >
                                PURCHASES
                            </Typography>
                        </Flex>
                        <Flex flex={1 / 3} justifyContent="center">
                            <Typography
                                color="#575757"
                                fontWeight="600"
                                fontSize="16px"
                                lineHeight="24px"
                            >
                                REFUNDS
                            </Typography>
                        </Flex>
                    </Flex>
                    <Flex justifyContent="space-between" flex={1} alignItems="center">
                        <Flex flex={1 / 3}>
                            <Typography color="#575757" fontWeight="400" fontSize="16px">
                                Intra Regional
                            </Typography>
                        </Flex>
                        <Flex flex={1 / 3} gap="24px" justifyContent="center">
                            <RateInputField
                                name={`${name}.locality.intraRegional.purchases.percentage`}
                                disabled
                                flexDirection="column"
                                label="%"
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                            <RateInputField
                                name={`${name}.locality.intraRegional.purchases.costPerItem`}
                                disabled
                                label="c Per Item"
                                flexDirection="column"
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                        </Flex>
                        <Flex flex={1 / 3} gap="24px" justifyContent="center">
                            <RateInputField
                                name={`${name}.locality.intraRegional.refunds.percentage`}
                                disabled
                                flexDirection="column"
                                label="%"
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                            <RateInputField
                                name={`${name}.locality.intraRegional.refunds.costPerItem`}
                                disabled
                                label="c Per Item"
                                flexDirection="column"
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                        </Flex>
                    </Flex>
                    <Flex
                        justifyContent="space-between"
                        flex={1}
                        alignItems="center"
                        marginTop="8px"
                    >
                        <Flex flex={1 / 3}>
                            <Typography color="#575757" fontWeight="400" fontSize="16px">
                                Inter Regional
                            </Typography>
                        </Flex>
                        <Flex flex={1 / 3} gap="24px" justifyContent="center">
                            <RateInputField
                                name={`${name}.locality.interRegional.purchases.percentage`}
                                disabled
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                            <RateInputField
                                name={`${name}.locality.interRegional.purchases.costPerItem`}
                                disabled
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                        </Flex>
                        <Flex flex={1 / 3} gap="24px" justifyContent="center">
                            <RateInputField
                                name={`${name}.locality.interRegional.refunds.percentage`}
                                disabled
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                            <RateInputField
                                name={`${name}.locality.interRegional.refunds.costPerItem`}
                                disabled
                                containerStyle={{ gap: '8px', textAlign: 'start' }}
                            />
                        </Flex>
                    </Flex>
                </Flex>
            </SectionCard>
        </Flex>
    );
};
